import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { usePaymentVerification } from '../../hooks/useStripe';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiBook, FiMail, FiArrowRight, FiDownload, FiX } = FiIcons;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { verifyPayment, loading } = usePaymentVerification();
  
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!sessionId) {
      setError('Invalid checkout session');
      return;
    }

    const handlePaymentVerification = async () => {
      const { data, error } = await verifyPayment(sessionId);
      
      if (error) {
        setError(error.message || 'Failed to verify payment');
      } else {
        setPaymentData(data);
      }
    };

    handlePaymentVerification();
  }, [sessionId, user, navigate, verifyPayment]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiX} className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Payment Verification Failed</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link
              to="/courses"
              className="inline-flex items-center space-x-2 bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              <span>Back to Courses</span>
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!paymentData) {
    return null;
  }

  const { course, enrollment, transaction } = paymentData;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden"
        >
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-12 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <SafeIcon icon={FiCheckCircle} className="w-20 h-20 text-white mx-auto mb-4" />
            </motion.div>
            <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-green-100">
              Welcome to your new nutrition course
            </p>
          </div>

          {/* Course Details */}
          <div className="px-8 py-8">
            <div className="flex items-start space-x-4 mb-8">
              {course.cover_image_url && (
                <img
                  src={course.cover_image_url}
                  alt={course.title}
                  className="w-24 h-24 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h2>
                <p className="text-gray-600 mb-4">{course.summary}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Lifetime Access</span>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Course Price:</span>
                  <span className="font-medium">${course.price}</span>
                </div>
                {transaction.discount_amount > 0 && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({transaction.coupon_code}):</span>
                      <span>-${transaction.discount_amount}</span>
                    </div>
                    <hr className="border-gray-200" />
                  </>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Paid:</span>
                  <span>${transaction.amount}</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="space-y-4 mb-8">
              <h3 className="text-lg font-semibold text-gray-900">What's Next?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3 p-4 bg-nutrition-50 rounded-lg">
                  <SafeIcon icon={FiBook} className="w-5 h-5 text-nutrition-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Start Learning</h4>
                    <p className="text-sm text-gray-600">Access your course content immediately</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-nutrition-50 rounded-lg">
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-nutrition-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Check Your Email</h4>
                    <p className="text-sm text-gray-600">Receipt and course details sent</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={`/course/${course.slug}`}
                className="flex-1 bg-nutrition-600 hover:bg-nutrition-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiBook} className="w-4 h-4" />
                <span>Start Course</span>
              </Link>
              <Link
                to="/dashboard"
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
              >
                <span>Go to Dashboard</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </div>

            {/* Receipt Download */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button className="text-nutrition-600 hover:text-nutrition-700 text-sm font-medium flex items-center space-x-1">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Download Receipt</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;